/**
 * IMPL-055 (2026-05-26) · Pestaña Funnel dentro de Analytics del AdminPanel.
 *
 * Visualiza los 8 hitos del recorrido landing → PDF como embudo vertical:
 * cada paso es una barra horizontal cuyo ancho es proporcional al % vs paso 1.
 *
 * Selector temporal: 7d / 30d / 90d.
 *
 * El componente vive en archivo separado (Regla 1: AdminPanel.jsx > 2000 líneas).
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Filter, TrendingDown, Target } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RANGOS = [
  { v: 7, label: "7 días" },
  { v: 30, label: "30 días" },
  { v: 90, label: "90 días" },
];

const fmtPct = (v) => `${(v ?? 0).toFixed(1)}%`;

const FunnelTab = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (d) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("mobilsafe_token");
      const res = await axios.get(`${API}/analytics/funnel?days=${d}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      toast.error("Error cargando funnel");
    }
    setLoading(false);
  };

  useEffect(() => {
    load(days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const baseCount = data?.steps?.[0]?.count || 0;

  return (
    <div data-testid="funnel-tab" className="space-y-6">
      {/* Header con selector temporal */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-cyan-600" />
          <span className="text-sm font-semibold text-cyan-700">Rango temporal:</span>
          <div className="flex gap-1" data-testid="funnel-range-selector">
            {RANGOS.map((r) => (
              <button
                key={r.v}
                data-testid={`funnel-range-${r.v}`}
                onClick={() => setDays(r.v)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  days === r.v
                    ? "bg-cyan-500 text-white shadow"
                    : "bg-cyan-500/10 text-cyan-700 hover:bg-cyan-500/20"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
        <button
          data-testid="funnel-refresh-btn"
          onClick={() => load(days)}
          disabled={loading}
          className="px-3 py-1.5 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-700 rounded-lg text-xs font-semibold flex items-center gap-2"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Actualizar
        </button>
      </div>

      {loading && !data ? (
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="w-8 h-8 text-cyan-400 animate-spin" />
        </div>
      ) : data ? (
        <>
          {/* Resumen superior */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-xl p-4">
              <p className="text-3xl font-black text-cyan-600" data-testid="funnel-kpi-visitors">
                {data.visitors_unique}
              </p>
              <p className="text-xs text-cyan-700/70 uppercase tracking-wide">
                Visitantes únicos
              </p>
            </div>
            <div className="bg-gradient-to-br from-emerald-500/20 to-green-500/20 border border-emerald-500/30 rounded-xl p-4">
              <p className="text-3xl font-black text-emerald-600" data-testid="funnel-kpi-converted">
                {data.steps?.[data.steps.length - 1]?.count || 0}
              </p>
              <p className="text-xs text-emerald-700/70 uppercase tracking-wide">
                Convertidos (PDF)
              </p>
            </div>
            <div className="bg-gradient-to-br from-purple-500/20 to-violet-500/20 border border-purple-500/30 rounded-xl p-4">
              <p className="text-3xl font-black text-purple-600" data-testid="funnel-kpi-conv-rate">
                {fmtPct(data.steps?.[data.steps.length - 1]?.conversion_pct)}
              </p>
              <p className="text-xs text-purple-700/70 uppercase tracking-wide">
                Conversión global
              </p>
            </div>
          </div>

          {/* Embudo vertical */}
          <div className="bg-slate-50 border border-cyan-500/20 rounded-xl p-4 md:p-6">
            <h3 className="text-sm font-bold text-cyan-700 mb-4 flex items-center gap-2">
              <Target className="w-4 h-4" /> Embudo de conversión · {days} días
            </h3>
            <div className="space-y-2" data-testid="funnel-steps-list">
              {data.steps.map((s, idx) => {
                const widthPct = baseCount > 0 ? (s.count / baseCount) * 100 : 0;
                const isLast = idx === data.steps.length - 1;
                return (
                  <div
                    key={s.step}
                    data-testid={`funnel-step-${s.step}`}
                    className="relative"
                  >
                    {/* Barra */}
                    <div className="relative h-12 bg-white border border-slate-200 rounded-lg overflow-hidden">
                      <div
                        className={`absolute inset-y-0 left-0 transition-all ${
                          isLast
                            ? "bg-gradient-to-r from-emerald-500 to-green-500"
                            : "bg-gradient-to-r from-cyan-500 to-blue-500"
                        }`}
                        style={{ width: `${Math.max(widthPct, 2)}%` }}
                      />
                      <div className="absolute inset-0 flex items-center justify-between px-3">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <span className="flex-shrink-0 w-6 h-6 bg-white/90 text-cyan-700 text-xs font-bold rounded-full flex items-center justify-center shadow">
                            {s.step}
                          </span>
                          <span className="text-xs md:text-sm font-semibold text-slate-800 truncate">
                            {s.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <span
                            className="text-sm font-black text-slate-800"
                            data-testid={`funnel-step-${s.step}-count`}
                          >
                            {s.count}
                          </span>
                          <span
                            className="hidden sm:inline-block text-xs font-semibold text-slate-600 w-14 text-right"
                            data-testid={`funnel-step-${s.step}-conv`}
                          >
                            {fmtPct(s.conversion_pct)}
                          </span>
                        </div>
                      </div>
                    </div>
                    {/* Drop-off entre pasos */}
                    {idx > 0 && s.dropoff_pct > 0 && (
                      <div
                        className="flex items-center gap-1 mt-0.5 mb-0.5 ml-9 text-[10px] font-semibold text-rose-600"
                        data-testid={`funnel-step-${s.step}-dropoff`}
                      >
                        <TrendingDown className="w-3 h-3" />
                        <span>{fmtPct(s.dropoff_pct)} drop vs paso {idx}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Nota explicativa */}
          <div className="text-xs text-slate-500 leading-relaxed">
            Los contadores muestran <strong>visitantes únicos</strong> por paso
            (no eventos brutos). El % de conversión se calcula respecto al paso 1.
            El drop-off compara cada paso con el inmediatamente anterior. Se
            excluyen visitas desde entornos de desarrollo y navegadores
            automatizados (mismo filtro que Analytics · Resumen).
          </div>
        </>
      ) : (
        <div className="text-center py-10 text-cyan-700">
          <Target className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No hay datos del funnel disponibles.</p>
        </div>
      )}
    </div>
  );
};

export default FunnelTab;
